/** Annotate → Queues: the queues I made or that were shared with me, with progress and who reviews;
 *  Active or Archived, with a ⋯ per row to archive or restore. */

import type { ReactNode } from "react";
import { Link } from "react-router";
import { Box } from "@/components/ui/box";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Progress } from "@/components/ui/page";
import { EmptyState, QueryState } from "@/components/ui/states";
import { formatCount } from "@/lib/format";
import { useQueues } from "./hooks";
import { initials } from "./scores";
import type { Queue } from "./types";
import { useProject } from "@/features/projects/hooks";
import { archiveColumn } from "@/features/archive/archive-menu";
import { ArchiveEmpty, ArchiveTabs } from "@/features/archive/archived";
import { useArchiveTabs } from "@/features/archive/hooks";

/** The owner first, then the people it was shared with. */
export function Reviewers({ owner, reviewers }: { owner: string; reviewers: string[] }) {
  const people = [owner, ...reviewers];
  return (
    <span className="flex items-center gap-1" aria-label={`Reviewers: ${people.join(", ")}`}>
      {people.map((email, i) => (
        <span
          key={email}
          title={i === 0 ? `${email} (owner)` : email}
          aria-hidden
          className="grid size-6 place-items-center rounded-full bg-avatar text-[10px] font-semibold text-body"
        >
          {initials(email)}
        </span>
      ))}
    </span>
  );
}

const COLUMNS: Column<Queue>[] = [
  {
    header: "Queue",
    cell: (q) => (
      <span className="flex min-w-0 flex-col gap-0.5">
        <Link to={`/annotation/${q.id}`} className="font-semibold hover:underline">
          {q.name}
        </Link>
        <span className="max-w-[460px] truncate text-xs text-muted" title={q.description}>
          {q.description || "No instructions"}
        </span>
      </span>
    ),
  },
  {
    header: "Scores",
    cell: (q) => (
      <span
        className="max-w-[260px] truncate text-xs text-muted"
        title={q.scores.map((s) => s.name).join(", ")}
      >
        {formatCount(q.scores.length)} · {q.scores.map((s) => s.name).join(", ")}
      </span>
    ),
  },
  {
    header: "Progress",
    headClass: "w-[200px]",
    cell: (q) => (
      <span className="flex items-center gap-2.5">
        <span className="flex-1">
          <Progress value={q.completed} max={q.total} label={`${q.name} progress`} />
        </span>
        <span className="font-mono text-xs text-muted">
          {q.completed}/{q.total}
        </span>
      </span>
    ),
  },
  { header: "Reviewers", cell: (q) => <Reviewers owner={q.created_by} reviewers={q.reviewers} /> },
  archiveColumn("queue"),
];

export function QueuesTab({ newButton }: { newButton: ReactNode }) {
  const tabs = useArchiveTabs("queue", useQueues());
  const queues = tabs.query;
  const archived = tabs.counts.archived ?? 0;
  const seesAll = useProject().can("annotations:read_all");
  return (
    <>
      <ArchiveTabs kind="queue" {...tabs.counts} />
      <Box>
        <QueryState query={queues} what="queues">
          {queues.data?.length ? (
            <DataTable columns={COLUMNS} rows={queues.data} />
          ) : tabs.view === "archived" || archived > 0 ? (
            <ArchiveEmpty kind="queue" view={tabs.view} archived={archived} create={newButton} />
          ) : (
            <EmptyState title="No annotation queues" action={newButton}>
              {seesAll
                ? "A queue collects experiment results for review, with the scores each reviewer gives."
                : "Nothing to review yet. Queues show up here when someone asks you to review them."}
            </EmptyState>
          )}
        </QueryState>
      </Box>
    </>
  );
}
